import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Settings, Sparkles, Activity, ChevronDown, LogOut, User, Database, BarChart3 } from 'lucide-react';
import { useMsal } from '@azure/msal-react';
import { useAuth } from '../../contexts/AuthContext';

interface ProfileDropdownProps {
  userName: string;
  userAvatar?: string;
}

const ProfileDropdown: React.FC<ProfileDropdownProps> = ({ userName, userAvatar }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { instance } = useMsal();
  const { userRole } = useAuth();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown when navigating
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const allDropdownItems = [
    {
      id: 'settings',
      label: 'Settings',
      icon: Settings,
      path: '/settings',
      adminOnly: true,
    },
    {
      id: 'prototypes',
      label: 'Data Prototypes',
      icon: BarChart3,
      path: '/prototypes',
      adminOnly: true,
    },
    {
      id: 'sample-data',
      label: 'Sample Data Seeding',
      icon: Database,
      path: '/admin/sample-data',
      adminOnly: true,
    },
    {
      id: 'demo',
      label: 'Components Demo',
      icon: Sparkles,
      path: '/demo',
      adminOnly: true,
    },
    {
      id: 'service-health',
      label: 'Service Health',
      icon: Activity,
      path: '/debug/auth',
      adminOnly: true,
    },
  ];

  // Filter menu items based on user role
  const dropdownItems = allDropdownItems.filter(item => 
    !item.adminOnly || userRole === 'Administrator'
  );

  const isActiveRoute = (path: string) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const handleLogout = (): void => {
    instance.logoutRedirect({
      postLogoutRedirectUri: "/",
    });
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Profile Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 text-gray-300 hover:text-white transition-colors p-2 rounded-md hover:bg-gray-700"
      >
        <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center overflow-hidden">
          {userAvatar ? (
            <img 
              src={userAvatar} 
              alt={userName} 
              className="w-8 h-8 rounded-full object-cover"
            />
          ) : (
            <User className="w-4 h-4 text-white" />
          )}
        </div>
        <span className="font-medium hidden sm:inline">{userName}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-50">
          <div className="py-1">
            {dropdownItems.map((item) => {
              const Icon = item.icon;
              const isActive = isActiveRoute(item.path);
              
              return (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`flex items-center space-x-3 px-4 py-2 text-sm transition-colors ${
                    isActive
                      ? 'text-white bg-blue-600'
                      : 'text-gray-300 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
            
            {/* Divider */}
            <div className="border-t border-gray-700 my-1"></div>
            
            {/* Sign Out */}
            <div className="px-4 py-2">
              <button 
                onClick={handleLogout}
                className="flex items-center space-x-3 text-sm text-gray-300 hover:text-white transition-colors w-full text-left"
              >
                <LogOut className="w-4 h-4" />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileDropdown;
